import React, { useEffect, useMemo } from 'react';
import { useSliderLogic } from '../../../hooks/useSliderLogic';
import InfoBlock from './InfoBlock';
import {
  SliderWrapper,
  AdvSliderTrack,
  AdvSlide,
  AdvArrowWrapper,
  AdvArrow,
  AdvIndicators,
  AdvIndicator
} from './styled';
import { useProjectFeatures } from '../../../hooks/useProjectFeatures';
import type { PlacementPageProps } from '../types';
import { AUTOPLAY_INTERVAL } from '../helpers';

export const PlacementPage: React.FC<PlacementPageProps> = ({
  placement,
  isVisible,
  hoveredSide,
  handleSlideMouseMove,
  handleArrowLeave,
  handleLeftArrowHover,
  handleRightArrowHover,
  t
}) => {
  const photos = placement?.photosUrls || [];
  const slider = useSliderLogic(
    photos.map(url => ({ image: url })),
    AUTOPLAY_INTERVAL,
    isVisible
  );

  useEffect(() => {
    slider.goToSlide(0);
    slider.setIsAutoplaying(true);
  }, [placement?.name]);

  const { featuresLeft, featuresRight } = useProjectFeatures(placement, t);

  const indicators = useMemo(() => {
    return photos.map((url, idx) => (
      <AdvIndicator
        key={url ?? idx}
        active={idx === slider.current}
        autoPlay={slider.isAutoplaying}
        onClick={() => slider.goToSlide(idx)}
        data-cy={`adv-indicator-${idx}`}
        aria-label={`Go to slide ${idx + 1}`}
        aria-current={idx === slider.current}
      >
        {slider.isAutoplaying && idx === slider.current && (
          <div
            className="progress"
            key={`${idx}-${slider.current}-${slider.isAutoplaying}`}
          />
        )}
      </AdvIndicator>
    ));
  }, [photos, slider.current, slider.isAutoplaying, photos]);

  return (
    <>
      <SliderWrapper
        data-ignore-parent-swipe="true"
        style={{ position: 'relative', width: '100%' }}
        onTouchStart={slider.handleTouchStart}
        onTouchMove={slider.handleTouchMove}
        onTouchEnd={slider.handleTouchEnd}
      >
        <AdvSliderTrack translate={-slider.current * 100}>
          {photos.map((url, idx) => (
            <AdvSlide
              key={url + idx}
              background={slider.loaded[idx] ? url : ''}
              onMouseMove={handleSlideMouseMove}
              onMouseLeave={handleArrowLeave}
              data-cy={`pasha-top-slide-${idx}`}
            >
              {!slider.loaded[idx] && <div className="placeholder" />}
            </AdvSlide>
          ))}
        </AdvSliderTrack>

        {photos.length > 1 && (
          <AdvArrowWrapper>
            <AdvArrow
              data-cy="slider-arrow-left"
              data-stat="advantages-slide-arrow"
              visible={hoveredSide === 'left'}
              onClick={slider.goToPrev}
              aria-label="prev photo"
              onMouseEnter={handleLeftArrowHover}
              onMouseLeave={handleArrowLeave}
            />
            <AdvArrow
              data-cy="slider-arrow-right"
              data-stat="advantages-slide-arrow"
              right
              visible={hoveredSide === 'right'}
              onClick={slider.goToNext}
              aria-label="next photo"
              onMouseEnter={handleRightArrowHover}
              onMouseLeave={handleArrowLeave}
            />
          </AdvArrowWrapper>
        )}
        {photos.length > 1 && <AdvIndicators>{indicators}</AdvIndicators>}
      </SliderWrapper>

      <InfoBlock
        title={placement.name}
        subtitle={placement.slogan || placement.coverSlogan}
        featuresLeft={featuresLeft}
        featuresRight={featuresRight}
        url={placement.path || placement.url}
        moreLabel={t('pasha.details')}
      />
    </>
  );
};
